import { Tooltip } from 'antd'
import { type RefObject, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

export const AVAILABILITY_NOTE_ATTR = 'data-availability-note'

type AvailabilityNoteTooltipProps = {
  // The element holding the Syncfusion grid; hover is watched there, not on every cell.
  containerRef: RefObject<HTMLElement | null>
}

type Box = { left: number; top: number; width: number; height: number }

type HoveredNote = {
  text: string
  box: Box
}

const noteOf = (target: EventTarget | null): { cell: HTMLTableCellElement; text: string } | null => {
  if (!(target instanceof Element)) return null
  const cell = target.closest(`.e-work-cells[${AVAILABILITY_NOTE_ATTR}]`)
  const text = cell?.getAttribute(AVAILABILITY_NOTE_ATTR)
  return cell instanceof HTMLTableCellElement && text ? { cell, text } : null
}

const sameNoteCell = (row: Element | null, index: number, text: string): HTMLElement | null => {
  const cell = row?.children[index]
  return cell instanceof HTMLElement && cell.getAttribute(AVAILABILITY_NOTE_ATTR) === text ? cell : null
}

// The block is every consecutive cell of that column with the same note, clipped to the grid's
// visible box, so the tooltip rests above the block instead of hopping between 15-minute cells.
const blockBox = (cell: HTMLTableCellElement, text: string, root: HTMLElement): Box => {
  const index = cell.cellIndex
  let first: HTMLElement = cell
  let last: HTMLElement = cell
  for (let row = cell.parentElement?.previousElementSibling ?? null; row; row = row.previousElementSibling) {
    const above = sameNoteCell(row, index, text)
    if (!above) break
    first = above
  }
  for (let row = cell.parentElement?.nextElementSibling ?? null; row; row = row.nextElementSibling) {
    const below = sameNoteCell(row, index, text)
    if (!below) break
    last = below
  }
  const head = first.getBoundingClientRect()
  const tail = last.getBoundingClientRect()
  const visible = root.getBoundingClientRect()
  const top = Math.max(head.top, visible.top)
  const bottom = Math.min(tail.bottom, visible.bottom)
  return { left: head.left, top, width: head.width, height: Math.max(bottom - top, 0) }
}

const sameBox = (left: Box, right: Box): boolean =>
  left.left === right.left && left.top === right.top && left.width === right.width && left.height === right.height

// Shows a block's full note the moment the mouse enters any of its cells; the grid cells are
// Syncfusion-owned, so the antd Tooltip is anchored to a fixed box laid over the hovered cell.
export const AvailabilityNoteTooltip = ({ containerRef }: AvailabilityNoteTooltipProps) => {
  const [hovered, setHovered] = useState<HoveredNote | null>(null)

  useEffect(() => {
    const root = containerRef.current
    if (!root) return undefined
    const onOver = (event: Event) => {
      const found = noteOf(event.target)
      if (!found) {
        setHovered(null)
        return
      }
      const box = blockBox(found.cell, found.text, root)
      setHovered((previous) =>
        previous && previous.text === found.text && sameBox(previous.box, box) ? previous : { text: found.text, box },
      )
    }
    const hide = () => setHovered(null)
    root.addEventListener('mouseover', onOver)
    root.addEventListener('mouseleave', hide)
    root.addEventListener('scroll', hide, true)
    return () => {
      root.removeEventListener('mouseover', onOver)
      root.removeEventListener('mouseleave', hide)
      root.removeEventListener('scroll', hide, true)
    }
  }, [containerRef])

  if (!hovered) return null
  const { box, text } = hovered
  return createPortal(
    <Tooltip open placement='top' title={text}>
      <span
        aria-hidden
        data-testid='availability-note-anchor'
        style={{ position: 'fixed', pointerEvents: 'none', ...box }}
      />
    </Tooltip>,
    document.body,
  )
}
